/**
 * Dataverse Web API Type Definitions
 *
 * Types for interacting with the Dataverse Web API.
 * Based on OData v4 protocol.
 */

// =============================================================================
// OData Response Types
// =============================================================================

/**
 * Standard OData collection response
 */
export interface ODataCollectionResponse<T> {
  '@odata.context': string;
  '@odata.count'?: number;
  '@odata.nextLink'?: string;
  value: T[];
}

/**
 * Standard OData single entity response
 */
export interface ODataEntityResponse {
  '@odata.context': string;
  '@odata.etag'?: string;
}

/**
 * OData error response
 */
export interface ODataError {
  error: {
    code: string;
    message: string;
    innererror?: {
      message: string;
      type: string;
      stacktrace: string;
    };
  };
}

// =============================================================================
// Query Options
// =============================================================================

/**
 * OData query options for list operations
 */
export interface QueryOptions {
  /** Fields to select (limits response payload) */
  select?: string[];
  /** Navigation properties to expand */
  expand?: string[] | ExpandOption[];
  /** OData filter expression */
  filter?: string;
  /** Order by expression */
  orderBy?: string;
  /** Number of records to return */
  top?: number;
  /** Number of records to skip */
  skip?: number;
  /** Include total count in response */
  count?: boolean;
  /** OData $apply for aggregation (groupby, aggregate, filter) */
  apply?: string;
}

/**
 * Expanded navigation property with nested options
 */
export interface ExpandOption {
  property: string;
  select?: string[];
  filter?: string;
  orderBy?: string;
  top?: number;
}

/**
 * Options for create/update operations
 */
export interface MutationOptions {
  /** Return the created/updated entity */
  returnRepresentation?: boolean;
  /** Optimistic concurrency check (ETag) */
  ifMatch?: string;
  /** Only create if doesn't exist */
  ifNoneMatch?: '*';
  /** Extra request headers (e.g. 'MSCRM.SuppressDuplicateDetection': 'false'
   *  so Dataverse enforces published duplicate-detection rules on create). */
  extraHeaders?: Record<string, string>;
}

// =============================================================================
// Dataverse Base Entity
// =============================================================================

/**
 * Common fields present on all Dataverse entities
 */
export interface DataverseEntity {
  /** Allow any property (Dataverse entities have dynamic shapes) */
  [key: string]: unknown;
  /** Row version for optimistic concurrency */
  '@odata.etag'?: string;
  /** Created timestamp */
  createdon?: string;
  /** Modified timestamp */
  modifiedon?: string;
  /** Created by user ID */
  _createdby_value?: string;
  /** Modified by user ID */
  _modifiedby_value?: string;
  /** Owner ID */
  _ownerid_value?: string;
  /** State code (0 = Active, 1 = Inactive) */
  statecode?: number;
  /** Status code */
  statuscode?: number;
}

// =============================================================================
// Veracity Benefits Catalog Entities (vba_ prefix, singular names)
// =============================================================================

/**
 * Publisher prefix for custom tables
 */
export const PUBLISHER_PREFIX = 'vba';

/**
 * Managed List - Reusable picklist definition
 */
export interface ManagedListEntity extends DataverseEntity {
  vba_managedlistid: string;
  vba_name: string;
  vba_displayorder: number;
}

// ManagedListItemEntity intentionally lives next to its only consumer in
// `entities/managed-list/api/managedListApi.ts` because the actual Dataverse
// schema uses `vba_name` (not `vba_value`) and `_vba_managedlist_value` (not
// `_vba_managedlistid_value`). A previous central definition here drifted
// from production and was removed 2026-04-28 to prevent re-introduction.

/**
 * Group Info Category - Category in the group info template
 */
export interface GICategoryEntity extends DataverseEntity {
  vba_gicategoryid: string;
  vba_name: string;
  vba_description?: string;
  vba_displayorder: number;
}

/**
 * Group Info Field - Field within a category
 */
export interface GIFieldEntity extends DataverseEntity {
  vba_gifieldid: string;
  vba_name: string;
  vba_fieldtype: FieldType;
  vba_isrequired: boolean;
  vba_hasnotes: boolean;
  vba_hasdaterange: boolean;
  vba_choices?: string; // JSON array
  vba_displayorder: number;
  /** Lookup to parent category */
  '_vba_gicategoryid_value': string;
  /** Optional lookup to managed list */
  '_vba_listreference_value'?: string;
  /** Navigation properties */
  vba_gicategoryid?: GICategoryEntity;
  vba_ListReference?: ManagedListEntity;
}

/**
 * Solution - Top-level product offering
 */
export interface SolutionEntity extends DataverseEntity {
  vba_solutionid: string;
  vba_name: string;
  vba_description?: string;
  vba_displayorder: number;
  vba_effectivestart?: string;
  vba_effectiveend?: string;
  vba_independentofplanyear?: boolean;
  /** Navigation property */
  vba_servicegroup_solutionid?: ServiceGroupEntity[];
}

/**
 * Service Group - Organizational unit within a solution
 */
export interface ServiceGroupEntity extends DataverseEntity {
  vba_servicegroupid: string;
  vba_name: string;
  vba_description?: string;
  vba_displayorder: number;
  /** Lookup to parent solution */
  '_vba_solutionid_value': string;
  /** Navigation properties */
  vba_solutionid?: SolutionEntity;
  vba_service_servicegroupid?: ServiceEntity[];
}

/**
 * Service - Individual service offering
 */
export interface ServiceEntity extends DataverseEntity {
  vba_serviceid: string;
  vba_name: string;
  vba_description?: string;
  vba_includedbydefault: boolean;
  vba_eligibility: EligibilityType;
  vba_eligibilitynotes?: string;
  vba_effectivestart?: string;
  vba_effectiveend?: string;
  vba_displayorder: number;
  /** Lookup to parent service group */
  '_vba_servicegroupid_value': string;
  /** Navigation properties */
  vba_servicegroupid?: ServiceGroupEntity;
  vba_servicelistrule_serviceid?: ServiceListRuleEntity[];
  vba_serviceoption_serviceid?: ServiceOptionEntity[];
}

/**
 * Service List Rule - Plan-based restriction on a service
 */
export interface ServiceListRuleEntity extends DataverseEntity {
  vba_servicelistruleid: string;
  vba_itemvalue: string;
  vba_relationship: RuleRelationship;
  /** Lookup to parent service */
  '_vba_serviceid_value': string;
  /** Lookup to managed list */
  '_vba_listid_value': string;
  /** Navigation properties */
  vba_serviceid?: ServiceEntity;
  vba_listid?: ManagedListEntity;
}

/**
 * Service Option - Configurable parameter for a service
 */
export interface ServiceOptionEntity extends DataverseEntity {
  vba_serviceoptionid: string;
  vba_name: string;
  vba_isrequired: boolean;
  vba_optiontype: OptionType;
  vba_scope: OptionScope;
  vba_choices?: string; // JSON array
  vba_displayorder: number;
  /** Lookup to parent service */
  '_vba_serviceid_value': string;
  /** Navigation property */
  vba_serviceid?: ServiceEntity;
}

/**
 * Admin Config - Generic key-value configuration with JSON payloads
 */
export interface AdminConfigEntity extends DataverseEntity {
  vba_adminconfigid: string;
  vba_name: string;
  vba_category: string;
  vba_displayname: string;
  vba_description?: string;
  vba_value: string; // JSON string
}

// =============================================================================
// Implementation Template Entities
// =============================================================================

/**
 * Implementation Template - Master template for implementation projects
 */
export interface ImpTemplateEntity extends DataverseEntity {
  vba_imptemplateid: string;
  vba_name: string;
  vba_impdescription?: string;
  vba_templatetype?: number;
  vba_impstatus?: number;
  vba_version?: string;
  vba_impdefaultduration?: number;
  vba_defaultcoordinatorrole?: string;
  // Navigation properties (expanded)
  vba_imptemplate_sections?: ImpTemplateSectionEntity[];
}

/**
 * Implementation Template Section - Section within a template
 */
export interface ImpTemplateSectionEntity extends DataverseEntity {
  vba_imptemplatesectionid: string;
  vba_name: string;
  vba_impsortorder?: number;
  vba_impdescription?: string;
  '_vba_template_value'?: string;
  // Navigation
  vba_imptemplatesection_tasks?: ImpTemplateTaskEntity[];
}

/**
 * Implementation Template Task - Task within a section
 */
export interface ImpTemplateTaskEntity extends DataverseEntity {
  vba_imptemplatetaskid: string;
  vba_name: string;
  vba_tasktype?: number;
  vba_impinstructions?: string;
  vba_impdefaultduration?: number;
  vba_defaultassigneerole?: string;
  vba_contactrole?: string;
  vba_imptags?: string;
  vba_impsortorder?: number;
  vba_isdocumenttask?: boolean;
  vba_isrequireddoc?: boolean;
  vba_vendorspecific?: boolean;
  vba_databindingentity?: string;
  vba_databindingfield?: string;
  vba_autocompleteondata?: boolean;
  vba_isgating?: boolean;
  vba_gatingrules?: string;
  vba_criticality?: number;
  vba_sharepointsubfolder?: string;
  vba_documentname?: string;
  '_vba_section_value'?: string;
  '_vba_parenttask_value'?: string;
}

/**
 * Implementation Template Dependency - Task dependency within a template
 */
export interface ImpTemplateDepEntity extends DataverseEntity {
  vba_imptemplatedepid: string;
  vba_name: string;
  vba_impdeptype?: number;
  vba_implagdays?: number;
  '_vba_predecessor_value'?: string;
  '_vba_successor_value'?: string;
}

/**
 * Implementation Solution Module - Links solutions to template sections
 */
export interface ImpSolutionModuleEntity extends DataverseEntity {
  vba_impsolutionmoduleid: string;
  vba_name: string;
  vba_solutioncategory?: string;
  vba_imptriggercond?: number;
  vba_insertsection?: string;
  '_vba_template_value'?: string;
  '_vba_sourcetemplate_value'?: string;
}

// =============================================================================
// Enums (as string literals for type safety)
// =============================================================================

export type FieldType = 'boolean' | 'select' | 'multi_select' | 'open' | 'numeric';

export type OptionType = 'boolean' | 'select' | 'multi_select' | 'open' | 'numeric';

export type OptionScope = 'service_level' | 'plan_level' | 'data';

export type RuleRelationship = 'required_by' | 'available_with' | 'not_available_with';

export type EligibilityType = 'all' | 'ee_only' | 'ee_spouse' | 'ee_family';

// =============================================================================
// Table Names (for API calls) - Singular naming convention with vba_ prefix
// =============================================================================

export const TABLES = {
  MANAGED_LIST: 'vba_managedlists',        // Plural set name for OData
  MANAGED_LIST_ITEM: 'vba_managedlistitems',
  GI_CATEGORY: 'vba_gicategories',
  GI_FIELD: 'vba_gifields',
  SOLUTION: 'vba_solutions',
  SERVICE_GROUP: 'vba_servicegroups',
  SERVICE: 'vba_services',
  SERVICE_LIST_RULE: 'vba_servicelistrules',
  SERVICE_OPTION: 'vba_serviceoptions',
  PARTNER: 'vba_partners',
  PARTNER_GROUP_REF: 'vba_partnergrouprefs',
  PARTNER_PLAN_REF: 'vba_partnerplanrefs',
  // Group ↔ vendor + group ↔ contact links
  GROUP_VENDOR: 'vba_groupvendors',
  GROUP_CONTACT_ROLE: 'vba_groupcontactroles',
  GROUP_TEAMMATE: 'vba_groupteammates',
  // Plan-design registry (impform B2): catalog defs + per-plan values
  PLAN_DESIGN_CATEGORY: 'vba_plandesigncategories',
  PLAN_DESIGN_FIELD: 'vba_plandesignfields',
  GROUP_PLAN: 'vba_groupplans',
  PLAN_DESIGN_VALUE: 'vba_plandesignvalues',
  // S/S/O display placements: plan-design field/category projected into the SSO view
  PLAN_DESIGN_SSO_DISPLAY: 'vba_plandesignssodisplays',
  DAYLIGHT_TEAM: 'vba_daylightteams',
  DAYLIGHT_GOAL: 'vba_daylightgoals',
  // Ops tables
  OPS_GROUP: 'vba_opsgroups',
  CONTRACT_YEAR: 'vba_contractyears',
  GROUP_SOLUTION: 'vba_groupsolutions',
  GROUP_SERVICE: 'vba_groupservices',
  GROUP_SERVICE_OPTION: 'vba_groupserviceoptions',
  GI_INSTANCE: 'vba_giinstances',
  RENEWAL: 'vba_renewals',
  SALES_QUESTIONNAIRE: 'vba_salesquestionnaires',
  GROUP_MONTHLY_METRIC: 'vba_groupmonthlymetrics',
  SAVED_VIEW: 'vba_savedviews',
  ADMIN_CONFIG: 'vba_adminconfigs',
  GEOCODE: 'vba_geocodes',
  // Implementation tables
  IMP_TEMPLATE: 'vba_imptemplates',
  IMP_TEMPLATE_SECTION: 'vba_imptemplatesections',
  IMP_TEMPLATE_TASK: 'vba_imptemplatetasks',
  IMP_TEMPLATE_DEP: 'vba_imptemplatedeps',
  IMP_TEMPLATE_SECTION_REF: 'vba_imptemplatesectionrefs',
  IMP_SOLUTION_MODULE: 'vba_impsolutionmodules',
  IMP_PROJECT: 'vba_impprojects',
  IMP_PROJECT_SECTION: 'vba_impprojectsections',
  IMP_PROJECT_TASK: 'vba_impprojecttasks',
  IMP_PROJECT_DEP: 'vba_impprojectdeps',
  IMP_PROJECT_NOTE: 'vba_impprojectnotes',
  IMP_PROJECT_DOC: 'vba_impprojectdocs',
  // Telemetry
  USER_ACTIVITY: 'vba_useractivities',
  // Marketing — Braze sync log + per-run summaries (services/braze-sync Function)
  BRAZE_SYNC_LOG: 'vba_brazesynclogs',
  BRAZE_SYNC_RUN: 'vba_brazesyncruns',
  // Email templates
  EMAIL_TEMPLATE: 'vba_emailtemplates',
  // Per-user personalization
  USER_PREFERENCE: 'vba_userpreferences',
  // In-app role assignments
  USER_ROLE_ASSIGNMENT: 'vba_userroleassignments',
  // Sales — Quick Activity Points
  QUICK_POINT_TRACKING: 'dts_quickpointtrackings',
  QUICK_POINT_TARGET: 'dts_quickpointtargets',
  // Standard Dataverse tables
  ANNOTATION: 'annotations',
  TASK: 'tasks',
  CONTACT: 'contacts',
} as const;

/**
 * Logical table names (singular, as defined in Dataverse)
 */
export const TABLE_LOGICAL_NAMES = {
  MANAGED_LIST: 'vba_managedlist',
  MANAGED_LIST_ITEM: 'vba_managedlistitem',
  GI_CATEGORY: 'vba_gicategory',
  GI_FIELD: 'vba_gifield',
  SOLUTION: 'vba_solution',
  SERVICE_GROUP: 'vba_servicegroup',
  SERVICE: 'vba_service',
  SERVICE_LIST_RULE: 'vba_servicelistrule',
  SERVICE_OPTION: 'vba_serviceoption',
  PARTNER: 'vba_partner',
  PARTNER_GROUP_REF: 'vba_partnergroupref',
  PARTNER_PLAN_REF: 'vba_partnerplanref',
  // Plan-design registry (impform B2)
  PLAN_DESIGN_CATEGORY: 'vba_plandesigncategory',
  PLAN_DESIGN_FIELD: 'vba_plandesignfield',
  GROUP_PLAN: 'vba_groupplan',
  PLAN_DESIGN_VALUE: 'vba_plandesignvalue',
  PLAN_DESIGN_SSO_DISPLAY: 'vba_plandesignssodisplay',
  // Ops tables
  OPS_GROUP: 'vba_opsgroup',
  CONTRACT_YEAR: 'vba_contractyear',
  GROUP_SOLUTION: 'vba_groupsolution',
  GROUP_SERVICE: 'vba_groupservice',
  GROUP_SERVICE_OPTION: 'vba_groupserviceoption',
  GI_INSTANCE: 'vba_giinstance',
  RENEWAL: 'vba_renewal',
  SALES_QUESTIONNAIRE: 'vba_salesquestionnaire',
  GROUP_MONTHLY_METRIC: 'vba_groupmonthlymetric',
  SAVED_VIEW: 'vba_savedview',
  ADMIN_CONFIG: 'vba_adminconfig',
  GEOCODE: 'vba_geocode',
  // Implementation tables
  IMP_TEMPLATE: 'vba_imptemplate',
  IMP_TEMPLATE_SECTION: 'vba_imptemplatesection',
  IMP_TEMPLATE_TASK: 'vba_imptemplatetask',
  IMP_TEMPLATE_DEP: 'vba_imptemplatedep',
  IMP_TEMPLATE_SECTION_REF: 'vba_imptemplatesectionref',
  IMP_SOLUTION_MODULE: 'vba_impsolutionmodule',
  IMP_PROJECT: 'vba_impproject',
  IMP_PROJECT_SECTION: 'vba_impprojectsection',
  IMP_PROJECT_TASK: 'vba_impprojecttask',
  IMP_PROJECT_DEP: 'vba_impprojectdep',
  IMP_PROJECT_NOTE: 'vba_impprojectnote',
  IMP_PROJECT_DOC: 'vba_impprojectdoc',
  // Per-user personalization
  USER_PREFERENCE: 'vba_userpreference',
  // In-app role assignments
  USER_ROLE_ASSIGNMENT: 'vba_userroleassignment',
  // Sales — Quick Activity Points
  QUICK_POINT_TRACKING: 'dts_quickpointtracking',
  QUICK_POINT_TARGET: 'dts_quickpointtarget',
} as const;

export type TableName = (typeof TABLES)[keyof typeof TABLES];
export type TableLogicalName = (typeof TABLE_LOGICAL_NAMES)[keyof typeof TABLE_LOGICAL_NAMES];
